import React from 'react'
import classNames from 'classnames'
import { RiThumbUpLine, RiThumbUpFill, RiThumbDownLine, RiThumbDownFill } from 'react-icons/ri'
import { FaSpinner } from 'react-icons/fa6'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'

import {
  CompletionRole,
  CompletionState,
  File,
  PilotContextType,
  PilotFunctionCall,
  FunctionCallState,
} from '../../types'
import Markdown from '../../components/Markdown'
import PilotContext from 'bundles/Pilot/contexts/PilotContext'
import { useAppDispatch, useTemporaryTooltip } from 'bundles/Pilot/hooks'
import { updateCompletion } from './actions'
import FileThumbnail from '../files/FileThumbnail'

enum Thumb {
  Up = 'UP', // eslint-disable-line no-unused-vars
  Down = 'DOWN', // eslint-disable-line no-unused-vars
}

type Props = {
  completionId?: number
  content: string
  files?: File[]
  rating: number
  role: CompletionRole
  state: CompletionState
  updating?: boolean
  functionCall?: PilotFunctionCall
}

function Bubble({
  completionId,
  content,
  files,
  rating,
  role,
  state,
  updating,
  functionCall,
}: Props) {
  const { agentKey } = React.useContext<PilotContextType>(PilotContext)
  const dispatch = useAppDispatch()

  const isThinking =
    (state === CompletionState.Processing && (!content || content === 'Thinking...')) ||
    (functionCall &&
      (functionCall.state === FunctionCallState.Pending ||
        functionCall.state === FunctionCallState.Executing))
  const showRatingActions =
    !isThinking && !functionCall && completionId && role === CompletionRole.Assistant
  const { show: showThumbsUpTooltip, trigger: triggerThumbsUpTooltip } = useTemporaryTooltip(500)
  const { show: showThumbsDownTooltip, trigger: triggerThumbsDownTooltip } =
    useTemporaryTooltip(500)

  const containerClassNames = classNames({
    'pilot-completions-bubble': true,
    'assistant': CompletionRole.Assistant === role,
    'user': CompletionRole.User === role,
    'processing': CompletionState.Processing === state,
    'completed': CompletionState.Completed === state,
    'failed': CompletionState.Failed === state,
    'thinking': isThinking,
    'function-call': !!functionCall,
  })

  const handleThumbClicked = (e, thumb: Thumb) => {
    e.preventDefault()

    if (!updating) {
      if (thumb === Thumb.Up) {
        triggerThumbsUpTooltip()
        dispatch(updateCompletion({ id: completionId, rating: 1 === rating ? 0 : 1 }))
      } else {
        triggerThumbsDownTooltip()
        dispatch(updateCompletion({ id: completionId, rating: -1 === rating ? 0 : -1 }))
      }
    }
  }

  const renderContent = () => {
    if (state === CompletionState.Processing && content === 'Thinking...') {
      return (
        <div className='pilot-thinking-message'>
          <FaSpinner className='fa-spinner' />
        </div>
      )
    }

    return content
  }

  return (
    <div className={containerClassNames}>
      <div className='pilot-completions-container'>
        <div className='pilot-completions-bubble__content-container'>
          <div className='pilot-completions-bubble__content'>
            {CompletionRole.Assistant === role &&
            !isThinking &&
            typeof renderContent() === 'string' ? (
              <Markdown
                agentKey={agentKey}
                content={renderContent() as string}
                completionId={completionId}
              />
            ) : (
              renderContent()
            )}
          </div>
        </div>

        {files ? (
          <div className='pilot-completions-bubble__files'>
            {files.map(file => (
              <FileThumbnail key={file.clientId} file={file} />
            ))}
          </div>
        ) : null}

        {showRatingActions && (
          <div className='pilot-completions-bubble__actions'>
            <OverlayTrigger
              placement='bottom'
              overlay={
                <Tooltip id={`tooltip-thumbs-up-${completionId || 'default'}`}>
                  Good response
                </Tooltip>
              }
              show={showThumbsUpTooltip || undefined}
              trigger={['hover', 'focus']}
            >
              <a
                href='#'
                onClick={e => handleThumbClicked(e, Thumb.Up)}
                style={{ outline: 'none' }}
              >
                {1 === rating ? <RiThumbUpFill size={20} /> : <RiThumbUpLine size={20} />}
              </a>
            </OverlayTrigger>
            <OverlayTrigger
              placement='bottom'
              overlay={
                <Tooltip id={`tooltip-thumbs-down-${completionId || 'default'}`}>
                  Bad response
                </Tooltip>
              }
              show={showThumbsDownTooltip || undefined}
              trigger={['hover', 'focus']}
            >
              <a
                href='#'
                onClick={e => handleThumbClicked(e, Thumb.Down)}
                style={{ outline: 'none' }}
              >
                {-1 === rating ? <RiThumbDownFill size={20} /> : <RiThumbDownLine size={20} />}
              </a>
            </OverlayTrigger>
          </div>
        )}
      </div>
    </div>
  )
}

export default Bubble
